import { keepPreviousData, useQuery } from "@tanstack/react-query";
import fetchToBackend, { buildQuery } from "@/utils/fetchToBackend";
import { companyKeys } from "@/lib/query-keys";
import type {
  CompanyItem,
  CompanyContract,
  CompanyListSummary,
  CompanyNetwork,
  CompanyPriceItem,
} from "@/types/domain";

export interface CompanyFilters {
  keywords?: string[];
  searchTerms?: string[];
  departmentIds?: number[];
  provinceIds?: number[];
  districtIds?: number[];
  subjects?: string[];
  dateFrom?: string | null;
  dateTo?: string | null;
  /** Monto adjudicado total mínimo, en soles. */
  minAmount?: number | null;
  /** Solo empresas que ganaron algo en los últimos N meses. */
  activeSinceMonths?: number | null;
  sortBy?: "resources" | "awarded";
}

/**
 * Página del listado de empresas. El backend resuelve filtros, orden por monto y
 * paginación, así que la página solo pinta lo que recibe.
 */
export function useCompanies(
  filters: CompanyFilters,
  page: number,
  size: number,
) {
  return useQuery({
    queryKey: companyKeys.list({ ...filters, page, size }),
    placeholderData: keepPreviousData,
    // Las empresas solo cambian cuando corre el import, no minuto a minuto.
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const query = buildQuery({ ...filters, page, size });
      const res = await fetchToBackend<any>(`/companies/getAll${query}`);
      const data = res?.data ?? res;
      return {
        companies: (data?.companies ?? []) as CompanyItem[],
        total: (data?.total ?? 0) as number,
        /** `null` sin recursos buscados: no hay nada que sumar. */
        summary: (data?.summary ?? null) as CompanyListSummary | null,
      };
    },
  });
}

/**
 * Una empresa con métricas + extracteddata (SUNAT/OSCE). La lista ya no
 * incluye extracteddata, así que el detalle tiene su propio endpoint.
 */
export function useCompany(companyId?: number) {
  return useQuery({
    queryKey: companyKeys.detail(companyId),
    enabled: companyId != null,
    queryFn: async () => {
      const res = await fetchToBackend<any>(`/companies/getById/${companyId}`);
      return (res?.data ?? res) as CompanyItem;
    },
  });
}

export function useCompanyContracts(companyId?: number, keywords?: string[]) {
  return useQuery({
    queryKey: companyKeys.contracts(companyId, keywords),
    enabled: companyId != null,
    queryFn: async () => {
      const query = buildQuery({ keywords });
      const res = await fetchToBackend<any>(
        `/companies/getContracts/${companyId}${query}`,
      );
      const data = res?.data ?? res;
      return (Array.isArray(data) ? data : []) as CompanyContract[];
    },
  });
}

export function useCompanyPriceItems(companyId?: number, enabled = true) {
  return useQuery({
    queryKey: companyKeys.priceItems(companyId),
    enabled: enabled && companyId != null,
    queryFn: async () => {
      const res = await fetchToBackend<any>(
        `/companies/getPriceItems/${companyId}`,
      );
      const data = res?.data ?? res;
      return (Array.isArray(data) ? data : []) as CompanyPriceItem[];
    },
  });
}

export function useCompanyNetwork(companyId?: number) {
  return useQuery({
    queryKey: companyKeys.network(companyId),
    enabled: companyId != null,
    queryFn: async () => {
      const res = await fetchToBackend<any>(
        `/companies/getNetwork/${companyId}`,
      );
      return (res?.data ?? res) as CompanyNetwork;
    },
  });
}
